
import { useState } from 'react';
import { motion } from 'framer-motion';
import InlineEdit from '../../../components/base/InlineEdit';

const STORAGE_KEY = 'lonetrs_protips';

const defaultTips = [
  {
    id: 1,
    icon: 'ri-calendar-check-line',
    title: 'Book Early for Best Rates',
    description: 'Schedule your move 4-6 weeks in advance to secure preferred dates and take advantage of early booking discounts. Peak moving season runs May through September.',
  },
  {
    id: 2,
    icon: 'ri-box-3-line',
    title: 'Declutter Before Packing',
    description: 'Reduce moving costs by donating or selling items you no longer need. Less weight means lower transportation costs and faster loading times.',
  },
  {
    id: 3,
    icon: 'ri-file-list-3-line',
    title: 'Create an Inventory List',
    description: 'Document all items being moved with photos and descriptions. This helps with insurance claims and ensures nothing gets left behind during the move.',
  },
  {
    id: 4,
    icon: 'ri-shield-check-line',
    title: 'Verify Insurance Coverage',
    description: 'Review your insurance options carefully. Consider additional coverage for high-value items like electronics, antiques, or artwork.',
  },
  {
    id: 5,
    icon: 'ri-tools-line',
    title: 'Prepare Appliances Properly',
    description: 'Disconnect and drain all appliances 24 hours before moving day. Secure loose parts and keep power cords accessible for easy reconnection.',
  },
  {
    id: 6,
    icon: 'ri-map-pin-2-line',
    title: 'Label Everything Clearly',
    description: 'Use color-coded labels for different rooms and mark fragile items prominently. Include handling instructions on boxes containing delicate contents.',
  },
];

type Tip = typeof defaultTips[0];

export default function ProTips() {
  const [tips, setTips] = useState<Tip[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultTips;
    } catch { return defaultTips; }
  });

  const save = (updated: Tip[]) => {
    setTips(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const updateField = (id: number, field: keyof Tip, value: string) => {
    save(tips.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const addTip = () => {
    const newTip: Tip = {
      id: Date.now(),
      icon: 'ri-lightbulb-line',
      title: 'New Tip Title',
      description: 'Click to edit this tip description and add your own expert advice.',
    };
    save([...tips, newTip]);
  };

  const deleteTip = (id: number) => {
    if (tips.length <= 1) return;
    save(tips.filter(t => t.id !== id));
  };

  return (
    <section className="py-24 bg-dark-800 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            PRO <span className="text-electric-blue">TIPS</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Expert advice to make your move smooth and efficient
          </p>
          <button
            onClick={addTip}
            className="mt-4 px-5 py-2 bg-electric-blue/20 border border-electric-blue/50 text-electric-blue font-inter text-sm rounded-full hover:bg-electric-blue/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
            Add Tip
          </button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tips.map((tip, index) => (
            <motion.div
              key={tip.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <div className="h-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/15 hover:border-electric-blue/50 transition-all duration-300 hover:shadow-xl hover:shadow-electric-blue/20 relative">
                {/* Delete button */}
                <button
                  onClick={() => deleteTip(tip.id)}
                  className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-red-400/40 hover:text-red-400 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>

                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${tip.icon} text-3xl text-electric-blue w-8 h-8 flex items-center justify-center`}></i>
                </div>

                <h3 className="font-orbitron text-lg font-bold text-white mb-3">
                  <InlineEdit
                    value={tip.title}
                    onSave={v => updateField(tip.id, 'title', v)}
                    className="text-white"
                    placeholder="Tip title..."
                  />
                </h3>

                <p className="font-inter text-white/70 text-sm leading-relaxed mb-4">
                  <InlineEdit
                    value={tip.description}
                    onSave={v => updateField(tip.id, 'description', v)}
                    multiline
                    rows={3}
                    className="text-white/70"
                    placeholder="Tip description..."
                  />
                </p>

                <button className="flex items-center space-x-2 text-electric-blue font-inter text-sm font-semibold hover:space-x-3 transition-all duration-300 cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
